import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'taxhealth_ref';
const COOKIE_KEY = 'taxhealth_ref';
const TTL_DAYS = 60;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;
const TTL_SECONDS = TTL_DAYS * 24 * 60 * 60;

interface StoredReferral {
  representative_id: string;
  slug: string;
  captured_at: number;
}

const normalizeSlug = (raw: string): string =>
  raw.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');

const isProdDomain = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /(^|\.)taxhealth\.com\.br$/.test(window.location.hostname);
};

const writeCookie = (payload: StoredReferral): void => {
  if (typeof document === 'undefined') return;
  try {
    const value = encodeURIComponent(JSON.stringify(payload));
    const parts = [
      `${COOKIE_KEY}=${value}`,
      `Max-Age=${TTL_SECONDS}`,
      `Path=/`,
      `SameSite=Lax`,
    ];
    if (isProdDomain()) {
      parts.push(`Domain=.taxhealth.com.br`);
      parts.push(`Secure`);
    }
    document.cookie = parts.join('; ');
  } catch {
    // ignore
  }
};

const readCookie = (): StoredReferral | null => {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${COOKIE_KEY}=`));
    if (!match) return null;
    const raw = decodeURIComponent(match.slice(COOKIE_KEY.length + 1));
    const parsed = JSON.parse(raw) as StoredReferral;
    if (!parsed?.representative_id) return null;
    if (Date.now() - parsed.captured_at > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const readLocalStorage = (): StoredReferral | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredReferral;
    if (Date.now() - parsed.captured_at > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeLocalStorage = (payload: StoredReferral): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
};

const readAny = (): StoredReferral | null => readCookie() ?? readLocalStorage();

const writeAll = (payload: StoredReferral): void => {
  writeCookie(payload);
  writeLocalStorage(payload);
};

/**
 * Reads `?ref=<slug>` from the current URL, resolves it to a representative_id
 * via the public RPC, and persists it in cookie + localStorage for 60 days.
 * Last-touch wins: any new ?ref= overwrites previous attribution.
 */
export const captureReferralFromUrl = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const rawSlug = params.get('ref');
  if (!rawSlug) return;

  const slug = normalizeSlug(rawSlug);
  if (!slug) return;

  const existing = readAny();
  if (existing && existing.slug === slug) {
    // Refresh TTL on revisit with same slug
    writeAll({ ...existing, captured_at: Date.now() });
    return;
  }

  try {
    const { data, error } = await (supabase as any).rpc('get_representative_id_by_slug', { _slug: slug });
    if (error || !data) {
      // invalid slug — ignore silently
      return;
    }
    writeAll({
      representative_id: data as string,
      slug,
      captured_at: Date.now(),
    });
  } catch {
    // network error — ignore silently
  }
};

/** Returns the stored representative_id if still within TTL, otherwise null. */
export const getStoredReferralId = (): string | null => {
  return readAny()?.representative_id ?? null;
};

export const getStoredReferralSlug = (): string | null => {
  return readAny()?.slug ?? null;
};
